import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { EnvelopeSimple, LockKey } from "@phosphor-icons/react";

const LOGO_URL = "https://customer-assets.emergentagent.com/wingman/d1fb5991-0166-4fe5-968c-76f807dc50fd/attachments/118b7de0ebb34bf293c8775031b95acc_Tarsus_Dijital_WP_PP.jpg.jpeg";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    if (result.success) navigate("/dashboard");
    else setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-[hsl(var(--background))]">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <img
              src={LOGO_URL}
              alt="Tarsus Dijital"
              className="h-20 w-20 rounded-2xl mb-8 object-cover ring-1 ring-[hsl(var(--border))] shadow-premium"
              data-testid="login-logo"
            />
            <h1 className="text-5xl font-bold text-[hsl(var(--text-strong))] mb-3 tracking-tighter">Tarsus Dijital</h1>
            <p className="text-base text-[hsl(var(--text-soft))] font-light">Reklam Ajansı Yönetim Paneli</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">E-posta</label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <EnvelopeSimple size={18} weight="duotone" className="text-[hsl(var(--text-faint))]" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] text-[hsl(var(--text-strong))] text-sm focus:border-[hsl(var(--text-strong))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--text-strong))]/10"
                  placeholder="admin@tarsus.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Şifre</label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <LockKey size={18} weight="duotone" className="text-[hsl(var(--text-faint))]" />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] text-[hsl(var(--text-strong))] text-sm focus:border-[hsl(var(--text-strong))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--text-strong))]/10"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div className="p-4 rounded-xl border border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger))]/5" data-testid="login-error-message">
                <p className="text-sm text-[hsl(var(--danger))]">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full py-3.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed shadow-premium premium-hover"
            >
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </button>
          </form>

          <p className="text-xs text-[hsl(var(--text-faint))] mt-8 text-center">© 2026 Tarsus Dijital Reklam Ajansı</p>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-[hsl(var(--primary))] items-center justify-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1482053450283-3e0b78b09a70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxibGFjayUyMGFuZCUyMHdoaXRlJTIwYWJzdHJhY3QlMjBnZW9tZXRyaWN8ZW58MHx8fHwxNzgwMjQ4NDI3fDA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 text-center px-12 max-w-lg">
          <img
            src={LOGO_URL}
            alt="Tarsus Dijital"
            className="h-36 w-36 mx-auto mb-10 rounded-3xl object-cover ring-1 ring-white/20 shadow-premium-xl"
          />
          <h2 className="text-5xl font-bold text-[hsl(var(--primary-foreground))] mb-4 tracking-tighter leading-tight">
            Premium Finansal<br />Yönetim
          </h2>
          <div className="w-12 h-px bg-[hsl(var(--primary-foreground))]/30 mx-auto my-8"></div>
          <p className="text-base text-[hsl(var(--primary-foreground))]/70 leading-relaxed font-light">
            Müşterilerinizi ve finansal verilerinizi profesyonel bir arayüzle yönetin.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
